import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Step1BasicInfo } from "@/components/profile-creation/Step1BasicInfo";
import { Step2ReligiousProfile } from "@/components/profile-creation/Step2ReligiousProfile";
import { Step3EducationCareer } from "@/components/profile-creation/Step3EducationCareer";
import { Step4LifestylePersonality } from "@/components/profile-creation/Step4LifestylePersonality";
import { Step5Preferences } from "@/components/profile-creation/Step5Preferences";
import { Step6ContactReview } from "@/components/profile-creation/Step6ContactReview";
import { QuickImport } from "@/components/profile-creation/QuickImport";
import { Loader2 } from "lucide-react";
import { ImportanceLevel } from "@/utils/weightedCompatibility";
import { DealbreakerQuestionInput } from "@/types/dealbreaker";
import { ProfileReference } from "@/lib/requiredFields";
import { normalizePhone } from "@/lib/phone";
import { useSaveDealbreakerQuestions, useDealbreakerQuestions } from "@/hooks/useDealbreakerQuestions";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { Home, User as UserIcon, Heart, Settings } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface ProfileFormData {
  // Basic Info
  first_name: string;
  age: number;
  gender: "male" | "female";
  location: string;
  education_level: string;
  occupation: string;
  height_feet?: number;
  height_inches?: number;
  ethnicity: string;
  citizenship_status: string;
  marital_status: string;
  has_children: boolean;
  children_count?: number;
  willing_to_relocate: boolean;
  profile_picture_url?: string;
  profile_pictures?: string[];
  
  // Religious Profile
  religiosity_level: "practicing" | "moderate" | "learning";
  prayer_frequency: string;
  wears_hijab?: boolean;
  importance_of_islam: number;
  sect: string;
  masjid_attendance: string;
  wears_hijab_level?: string;
  has_beard?: string;
  quran_knowledge: string;
  islamic_knowledge_level: string;
  
  // Education & Career
  field_of_study?: string;
  employment_status: string;
  financial_status: number;
  career_description?: string;
  
  // Lifestyle & Personality
  personality_type: number;
  personality_description: string;
  dietary_preferences: string[];
  smoking: string;
  exercise_routine?: string;
  bio: string;
  matchmaker_notes?: string;
  
  // Seeking
  seeking_age_min: number;
  seeking_age_max: number;
  seeking_height_min_feet?: number;
  seeking_height_min_inches?: number;
  seeking_height_max_feet?: number;
  seeking_height_max_inches?: number;
  seeking_ethnicity: string[];
  seeking_sect: string[];
  seeking_religiosity: string[];
  seeking_masjid_attendance_min?: string;
  seeking_hijab_preference?: string;
  seeking_beard_preference?: string;
  seeking_citizenship?: string[];
  seeking_marital_status: string[];
  seeking_children_ok: boolean;
  seeking_convert_ok: boolean;
  seeking_divorced_ok: boolean;
  seeking_personality_min?: number;
  seeking_personality_max?: number;
  seeking_financial_min?: number;
  seeking_education: string[];
  seeking_location_preference?: string;
  seeking_description: string;
  seeking_preferences_weights?: Record<string, ImportanceLevel>;
  
  // Community & References (in-depth signup)
  community_connection?: string;
  references?: ProfileReference[];

  // Contact Info
  email: string;
  phone: string;
  whatsapp: string;
  preferred_contact_method: string;
  wali_name?: string;
  wali_email?: string;
  wali_phone?: string;
  reveal_wali?: boolean;
  agree_terms?: boolean;
  agree_privacy?: boolean;
  understand_review?: boolean;
  affirm_truthful?: boolean;

  // Dealbreaker Questions
  dealbreaker_questions?: DealbreakerQuestionInput[];
}

const STEPS = [
  { title: "Basic Info", description: "Tell us about yourself" },
  { title: "Religious Profile", description: "Your faith and practice" },
  { title: "Education & Career", description: "Your professional background" },
  { title: "Lifestyle & Personality", description: "Your interests and values" },
  { title: "What You're Looking For", description: "Your preferences" },
  { title: "Contact & Review", description: "Final details" },
];

export default function ProfileCreation() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Dealbreaker questions hooks
  const { data: existingQuestions } = useDealbreakerQuestions(user?.id);
  const { mutateAsync: saveQuestions } = useSaveDealbreakerQuestions();
  
  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: "",
    age: 25,
    gender: "male",
    location: "",
    education_level: "",
    occupation: "",
    ethnicity: "Prefer not to say",
    citizenship_status: "US Citizen",
    marital_status: "Never Married",
    has_children: false,
    willing_to_relocate: false,
    religiosity_level: "moderate",
    prayer_frequency: "working on it",
    importance_of_islam: 5,
    sect: "Sunni",
    masjid_attendance: "Jumuah sometimes",
    quran_knowledge: "Basic",
    islamic_knowledge_level: "Basic",
    employment_status: "Employed full-time",
    financial_status: 3,
    personality_type: 5,
    personality_description: "",
    dietary_preferences: [],
    smoking: "Never",
    bio: "",
    seeking_age_min: 22,
    seeking_age_max: 35,
    seeking_ethnicity: [],
    seeking_sect: [],
    seeking_marital_status: [],
    seeking_children_ok: true,
    seeking_convert_ok: true,
    seeking_divorced_ok: true,
    seeking_religiosity: [],
    seeking_education: [],
    seeking_location_preference: "",
    seeking_description: "",
    email: user?.email || "",
    phone: "",
    whatsapp: "",
    // Must match the contact_info CHECK constraint casing exactly.
    preferred_contact_method: "Email",
  });

  // Check if profile already exists and load draft
  useEffect(() => {
    const loadExistingProfile = async () => {
      if (!user) return;
      
      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();

        if (error && error.code !== "PGRST116") throw error;

        if (profile) {
          // Mark as editing existing profile
          setIsEditingExisting(true);

          // Load existing profile data
          setFormData(prev => ({
            ...prev,
            // Basic Info
            first_name: profile.first_name || "",
            age: profile.age || 25,
            gender: (profile.gender as "male" | "female") || "male",
            location: profile.location || "",
            education_level: profile.education_level || "",
            occupation: profile.occupation || "",
            height_feet: profile.height_feet,
            height_inches: profile.height_inches,
            ethnicity: profile.ethnicity || "Prefer not to say",
            citizenship_status: profile.citizenship_status || "US Citizen",
            marital_status: profile.marital_status || "Never Married",
            has_children: profile.has_children || false,
            children_count: profile.children_count,
            willing_to_relocate: profile.willing_to_relocate || false,
            profile_picture_url: profile.profile_picture_url,
            profile_pictures: profile.profile_pictures || [],
            // Religious Profile
            religiosity_level: (profile.religiosity_level as "practicing" | "moderate" | "learning") || "moderate",
            prayer_frequency: profile.prayer_frequency || "working on it",
            wears_hijab: profile.wears_hijab || false,
            importance_of_islam: profile.importance_of_islam || 5,
            sect: profile.sect || "Sunni",
            masjid_attendance: profile.masjid_attendance || "Jumuah sometimes",
            wears_hijab_level: profile.wears_hijab_level || (profile.gender === "female" ? "No" : undefined),
            has_beard: profile.has_beard || (profile.gender === "male" ? "Clean shaven" : undefined),
            quran_knowledge: profile.quran_knowledge || "Basic",
            islamic_knowledge_level: profile.islamic_knowledge_level || "Basic",
            // Education & Career
            field_of_study: profile.field_of_study,
            employment_status: profile.employment_status || "Employed full-time",
            financial_status: profile.financial_status || 3,
            career_description: profile.career_description,
            // Lifestyle & Personality
            personality_type: profile.personality_type || 5,
            personality_description: profile.personality_description || "",
            dietary_preferences: profile.dietary_preferences || [],
            smoking: profile.smoking || "Never",
            exercise_routine: profile.exercise_routine,
            bio: profile.bio || "",
            matchmaker_notes: (profile as any).matchmaker_notes || "",
            // Seeking Preferences
            seeking_age_min: profile.seeking_age_min || 22,
            seeking_age_max: profile.seeking_age_max || 35,
            seeking_height_min_feet: profile.seeking_height_min_feet,
            seeking_height_min_inches: profile.seeking_height_min_inches,
            seeking_height_max_feet: profile.seeking_height_max_feet,
            seeking_height_max_inches: profile.seeking_height_max_inches,
            seeking_ethnicity: profile.seeking_ethnicity || [],
            seeking_sect: profile.seeking_sect || [],
            seeking_islam_importance_min: profile.seeking_islam_importance_min,
            seeking_masjid_attendance_min: profile.seeking_masjid_attendance_min,
            seeking_hijab_preference: profile.seeking_hijab_preference,
            seeking_beard_preference: profile.seeking_beard_preference,
            seeking_citizenship: profile.seeking_citizenship || [],
            seeking_marital_status: profile.seeking_marital_status || [],
            seeking_children_ok: profile.seeking_children_ok !== undefined ? profile.seeking_children_ok : true,
            seeking_convert_ok: profile.seeking_convert_ok !== undefined ? profile.seeking_convert_ok : true,
            seeking_divorced_ok: profile.seeking_divorced_ok !== undefined ? profile.seeking_divorced_ok : true,
            seeking_personality_min: profile.seeking_personality_min,
            seeking_personality_max: profile.seeking_personality_max,
            seeking_financial_min: profile.seeking_financial_min,
            seeking_religiosity: profile.seeking_religiosity || [],
            seeking_education: profile.seeking_education || [],
            seeking_location_preference: profile.seeking_location_preference || "",
            seeking_description: profile.seeking_description || "",
            community_connection: (profile as any).community_connection || "",
          }));
        }

        // Load existing references if any
        const { data: existingReferences } = await supabase
          .from("profile_references")
          .select("name, relationship, phone, email")
          .eq("profile_id", user.id);

        if (existingReferences && existingReferences.length > 0) {
          setFormData(prev => ({
            ...prev,
            references: existingReferences.map((r: any) => ({
              name: r.name || "",
              relationship: r.relationship || "",
              phone: r.phone || "",
              email: r.email || "",
            })),
          }));
        }

        // Load contact info if exists
        const { data: contactInfo } = await supabase
          .from("contact_info")
          .select("*")
          .eq("profile_id", user.id)
          .maybeSingle();

        if (contactInfo) {
          setFormData(prev => ({
            ...prev,
            email: contactInfo.email || user.email || "",
            phone: contactInfo.phone || "",
            whatsapp: contactInfo.whatsapp || "",
            preferred_contact_method: contactInfo.preferred_contact_method || "Email",
            wali_name: contactInfo.wali_name || "",
            wali_email: contactInfo.wali_email || "",
            wali_phone: contactInfo.wali_phone || "",
            reveal_wali: contactInfo.reveal_wali ?? false,
            // Load checkboxes from localStorage
            agree_terms: localStorage.getItem(`checkbox_agree_terms_${user.id}`) === 'true',
            agree_privacy: localStorage.getItem(`checkbox_agree_privacy_${user.id}`) === 'true',
            understand_review: localStorage.getItem(`checkbox_understand_review_${user.id}`) === 'true',
            affirm_truthful: localStorage.getItem(`checkbox_affirm_truthful_${user.id}`) === 'true',
          }));
        } else {
          // No contact info yet, still load checkboxes from localStorage
          setFormData(prev => ({
            ...prev,
            email: user.email || "",
            agree_terms: localStorage.getItem(`checkbox_agree_terms_${user.id}`) === 'true',
            agree_privacy: localStorage.getItem(`checkbox_agree_privacy_${user.id}`) === 'true',
            understand_review: localStorage.getItem(`checkbox_understand_review_${user.id}`) === 'true',
            affirm_truthful: localStorage.getItem(`checkbox_affirm_truthful_${user.id}`) === 'true',
          }));
        }
      } catch (error: any) {
        console.error("profile_creation.load_failed");
        toast.error("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    loadExistingProfile();
  }, [user, navigate]);

  // Load dealbreaker questions when available
  useEffect(() => {
    if (existingQuestions && existingQuestions.length > 0) {
      setFormData(prev => ({
        ...prev,
        dealbreaker_questions: existingQuestions.map(q => ({
          question_text: q.question_text,
          question_order: q.question_order,
        })),
      }));
    }
  }, [existingQuestions]);

  const saveDraft = async (data: Partial<ProfileFormData>) => {
    if (!user) return;
    
    setSaving(true);
    try {
      // Free-text columns (no CHECK constraint): null/undefined → "" so the DB
      // never sees a bare null where a string is expected.
      const sanitizeOptionalString = (val: any) => val ?? "";
      // Optional enum columns with CHECK (col IS NULL OR col IN (...)): must stay
      // NULL when unset — the constraints allow NULL but reject empty string.
      const nullableEnum = (val: any) => val || null;

      // Save profile data
      const { error: profileError } = await supabase
        .from("profiles")
        .upsert({
          id: user.id,
          // Required constraint fields
          status: "active",
          owned_by: user.id,
          // Basic Info
          first_name: data.first_name,
          age: data.age,
          gender: data.gender,
          location: data.location,
          education_level: data.education_level,
          occupation: sanitizeOptionalString(data.occupation),
          height_feet: data.height_feet,
          height_inches: data.height_inches,
          ethnicity: data.ethnicity,
          citizenship_status: data.citizenship_status,
          marital_status: data.marital_status,
          has_children: data.has_children,
          children_count: data.children_count,
          willing_to_relocate: data.willing_to_relocate,
          profile_picture_url: sanitizeOptionalString(data.profile_picture_url),
          profile_pictures: data.profile_pictures,
          // Religious Profile
          religiosity_level: data.religiosity_level,
          prayer_frequency: data.prayer_frequency,
          wears_hijab: data.wears_hijab,
          importance_of_islam: data.importance_of_islam,
          sect: data.sect,
          masjid_attendance: data.masjid_attendance,
          wears_hijab_level: nullableEnum(data.wears_hijab_level),
          has_beard: nullableEnum(data.has_beard),
          quran_knowledge: data.quran_knowledge,
          islamic_knowledge_level: data.islamic_knowledge_level,
          // Education & Career
          field_of_study: sanitizeOptionalString(data.field_of_study),
          employment_status: data.employment_status,
          financial_status: data.financial_status,
          career_description: sanitizeOptionalString(data.career_description),
          // Lifestyle & Personality
          personality_type: data.personality_type,
          personality_description: data.personality_description,
          dietary_preferences: data.dietary_preferences,
          smoking: data.smoking,
          exercise_routine: sanitizeOptionalString(data.exercise_routine),
          bio: sanitizeOptionalString(data.bio),
          // Seeking Preferences
          seeking_age_min: data.seeking_age_min,
          seeking_age_max: data.seeking_age_max,
          seeking_height_min_feet: data.seeking_height_min_feet,
          seeking_height_min_inches: data.seeking_height_min_inches,
          seeking_height_max_feet: data.seeking_height_max_feet,
          seeking_height_max_inches: data.seeking_height_max_inches,
          seeking_ethnicity: data.seeking_ethnicity,
          seeking_sect: data.seeking_sect,
          seeking_religiosity: data.seeking_religiosity,
          seeking_masjid_attendance_min: nullableEnum(data.seeking_masjid_attendance_min),
          seeking_location_preference: sanitizeOptionalString(data.seeking_location_preference),
          seeking_hijab_preference: nullableEnum(data.seeking_hijab_preference),
          seeking_beard_preference: nullableEnum(data.seeking_beard_preference),
          seeking_citizenship: data.seeking_citizenship,
          seeking_marital_status: data.seeking_marital_status,
          seeking_children_ok: data.seeking_children_ok,
          seeking_convert_ok: data.seeking_convert_ok,
          seeking_divorced_ok: data.seeking_divorced_ok,
          seeking_personality_min: data.seeking_personality_min,
          seeking_personality_max: data.seeking_personality_max,
          seeking_financial_min: data.seeking_financial_min,
          seeking_education: data.seeking_education,
          seeking_description: sanitizeOptionalString(data.seeking_description),
          matchmaker_notes: sanitizeOptionalString(data.matchmaker_notes),
          community_connection: sanitizeOptionalString(data.community_connection),
          // For new profiles, mark as incomplete. For existing profiles, keep completed status
          ...(isEditingExisting ? {} : { profile_completed: false }),
        });

      if (profileError) throw profileError;

      // Save contact info if provided (explicitly exclude checkbox fields)
      if (data.email || data.phone || data.whatsapp || data.preferred_contact_method || data.wali_name || data.wali_email || data.wali_phone || data.reveal_wali !== undefined) {
        const contactData: {
          profile_id: string;
          email?: string;
          phone?: string | null;
          whatsapp?: string | null;
          preferred_contact_method?: string;
          wali_name?: string;
          wali_email?: string;
          wali_phone?: string;
          reveal_wali?: boolean;
        } = {
          profile_id: user.id,
        };

        if (data.email) contactData.email = data.email;
        // Phones are stored normalized (E.164 where parseable) — see @/lib/phone.
        if (data.phone) contactData.phone = normalizePhone(data.phone);
        if (data.whatsapp) contactData.whatsapp = normalizePhone(data.whatsapp);
        if (data.preferred_contact_method) contactData.preferred_contact_method = data.preferred_contact_method;
        contactData.wali_name = sanitizeOptionalString(data.wali_name);
        contactData.wali_email = sanitizeOptionalString(data.wali_email);
        contactData.wali_phone = normalizePhone(data.wali_phone) ?? "";
        if (data.reveal_wali !== undefined) contactData.reveal_wali = data.reveal_wali;

        const { error: contactError } = await supabase
          .from("contact_info")
          .upsert(contactData, {
            onConflict: 'profile_id'
          });

        if (contactError) console.error("profile_creation.save_contact_draft_failed");
      }
      
      // Save checkboxes to localStorage
      if (data.agree_terms !== undefined) {
        localStorage.setItem(`checkbox_agree_terms_${user.id}`, String(data.agree_terms));
      }
      if (data.agree_privacy !== undefined) {
        localStorage.setItem(`checkbox_agree_privacy_${user.id}`, String(data.agree_privacy));
      }
      if (data.understand_review !== undefined) {
        localStorage.setItem(`checkbox_understand_review_${user.id}`, String(data.understand_review));
      }
      if (data.affirm_truthful !== undefined) {
        localStorage.setItem(`checkbox_affirm_truthful_${user.id}`, String(data.affirm_truthful));
      }

      // Save dealbreaker questions if provided (silent mode for auto-save)
      if (data.dealbreaker_questions !== undefined) {
        await saveQuestions({
          profileId: user.id,
          questions: data.dealbreaker_questions,
          silent: true,
        });
      }
    } catch (error: any) {
      console.error("profile_creation.save_draft_failed");
      toast.error("Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handleQuickImport = (extractedData: Partial<ProfileFormData>) => {
    setFormData(prev => ({ ...prev, ...extractedData }));
    setHasUnsavedChanges(true);
  };

  const handleStepComplete = async (stepData: Partial<ProfileFormData>) => {
    const updatedData = { ...formData, ...stepData };
    setFormData(updatedData);
    setHasUnsavedChanges(true);

    // Auto-save draft
    await saveDraft(updatedData);
    setHasUnsavedChanges(false);

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNavigationAttempt = async (path: string) => {
    // If profile is already complete, always allow navigation (auto-save if needed)
    if (isEditingExisting) {
      // Always save before navigating to ensure changes are persisted
      await saveDraft(formData);
      setHasUnsavedChanges(false);
      navigate(path);
      return;
    }

    // For new profiles in creation, show unsaved dialog if there are changes
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowUnsavedDialog(true);
    } else {
      navigate(path);
    }
  };

  const handleSaveAndNavigate = async () => {
    if (pendingNavigation) {
      // Save current step data
      await saveDraft(formData);
      setHasUnsavedChanges(false);
      setShowUnsavedDialog(false);
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleDiscardAndNavigate = () => {
    if (pendingNavigation) {
      setHasUnsavedChanges(false);
      setShowUnsavedDialog(false);
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleFinalSubmit = async (contactData: Partial<ProfileFormData>) => {
    if (!user) return;
    
    setSaving(true);
    const finalData = { ...formData, ...contactData };
    
    try {
      // Free-text columns (no CHECK constraint): null/undefined → "" so the DB
      // never sees a bare null where a string is expected.
      const sanitizeOptionalString = (val: any) => val ?? "";
      // Optional enum columns with CHECK (col IS NULL OR col IN (...)): must stay
      // NULL when unset — the constraints allow NULL but reject empty string.
      const nullableEnum = (val: any) => val || null;

      // Save complete profile
      const { error: profileError } = await supabase
        .from("profiles")
        .upsert({
          id: user.id,
          status: "active",
          owned_by: user.id,
          // Basic Info
          first_name: finalData.first_name,
          age: finalData.age,
          gender: finalData.gender,
          location: finalData.location,
          education_level: finalData.education_level,
          occupation: sanitizeOptionalString(finalData.occupation),
          height_feet: finalData.height_feet,
          height_inches: finalData.height_inches,
          ethnicity: finalData.ethnicity,
          citizenship_status: finalData.citizenship_status,
          marital_status: finalData.marital_status,
          has_children: finalData.has_children,
          children_count: finalData.children_count,
          willing_to_relocate: finalData.willing_to_relocate,
          profile_picture_url: sanitizeOptionalString(finalData.profile_picture_url),
          profile_pictures: finalData.profile_pictures,
          // Religious Profile
          religiosity_level: finalData.religiosity_level,
          prayer_frequency: finalData.prayer_frequency,
          wears_hijab: finalData.wears_hijab,
          importance_of_islam: finalData.importance_of_islam,
          sect: finalData.sect,
          masjid_attendance: finalData.masjid_attendance,
          wears_hijab_level: nullableEnum(finalData.wears_hijab_level),
          has_beard: nullableEnum(finalData.has_beard),
          quran_knowledge: finalData.quran_knowledge,
          islamic_knowledge_level: finalData.islamic_knowledge_level,
          // Education & Career
          field_of_study: sanitizeOptionalString(finalData.field_of_study),
          employment_status: finalData.employment_status,
          financial_status: finalData.financial_status,
          career_description: sanitizeOptionalString(finalData.career_description),
          // Lifestyle & Personality
          personality_type: finalData.personality_type,
          personality_description: finalData.personality_description,
          dietary_preferences: finalData.dietary_preferences,
          smoking: finalData.smoking,
          exercise_routine: sanitizeOptionalString(finalData.exercise_routine),
          bio: sanitizeOptionalString(finalData.bio),
          // Seeking Preferences
          seeking_age_min: finalData.seeking_age_min,
          seeking_age_max: finalData.seeking_age_max,
          seeking_height_min_feet: finalData.seeking_height_min_feet,
          seeking_height_min_inches: finalData.seeking_height_min_inches,
          seeking_height_max_feet: finalData.seeking_height_max_feet,
          seeking_height_max_inches: finalData.seeking_height_max_inches,
          seeking_ethnicity: finalData.seeking_ethnicity,
          seeking_sect: finalData.seeking_sect,
          seeking_religiosity: finalData.seeking_religiosity,
          seeking_masjid_attendance_min: nullableEnum(finalData.seeking_masjid_attendance_min),
          seeking_location_preference: sanitizeOptionalString(finalData.seeking_location_preference),
          seeking_hijab_preference: nullableEnum(finalData.seeking_hijab_preference),
          seeking_beard_preference: nullableEnum(finalData.seeking_beard_preference),
          seeking_citizenship: finalData.seeking_citizenship,
          seeking_marital_status: finalData.seeking_marital_status,
          seeking_children_ok: finalData.seeking_children_ok,
          seeking_convert_ok: finalData.seeking_convert_ok,
          seeking_divorced_ok: finalData.seeking_divorced_ok,
          seeking_personality_min: finalData.seeking_personality_min,
          seeking_personality_max: finalData.seeking_personality_max,
          seeking_financial_min: finalData.seeking_financial_min,
          seeking_education: finalData.seeking_education,
          seeking_description: sanitizeOptionalString(finalData.seeking_description),
          matchmaker_notes: sanitizeOptionalString(finalData.matchmaker_notes),
          community_connection: sanitizeOptionalString(finalData.community_connection),
          profile_completed: true,
          is_active: true,
        });

      if (profileError) throw profileError;

      // Save contact info (explicitly exclude checkbox fields - they're in localStorage)
      const contactData: {
        profile_id: string;
        email?: string;
        phone?: string | null;
        whatsapp?: string | null;
        preferred_contact_method?: string;
        wali_name?: string;
        wali_email?: string;
        wali_phone?: string;
        reveal_wali?: boolean;
      } = {
        profile_id: user.id,
      };

      if (finalData.email) contactData.email = finalData.email;
      // Phones are stored normalized (E.164 where parseable) — see @/lib/phone.
      if (finalData.phone) contactData.phone = normalizePhone(finalData.phone);
      if (finalData.whatsapp) contactData.whatsapp = normalizePhone(finalData.whatsapp);
      if (finalData.preferred_contact_method) contactData.preferred_contact_method = finalData.preferred_contact_method;
      contactData.wali_name = sanitizeOptionalString(finalData.wali_name);
      contactData.wali_email = sanitizeOptionalString(finalData.wali_email);
      contactData.wali_phone = normalizePhone(finalData.wali_phone) ?? "";
      if (finalData.reveal_wali !== undefined) contactData.reveal_wali = finalData.reveal_wali;

      const { error: contactError } = await supabase
        .from("contact_info")
        .upsert(contactData, {
          onConflict: 'profile_id'
        });

      if (contactError) throw contactError;

      // Save references (third-party PII). Replace any existing rows with the
      // current set so re-submits stay idempotent. Only persist valid ones.
      if (finalData.references && finalData.references.length > 0) {
        const validRefs = finalData.references.filter(
          (r) =>
            r.name?.trim() &&
            r.relationship?.trim() &&
            ((r.phone && r.phone.trim()) || (r.email && r.email.trim())),
        );
        if (validRefs.length > 0) {
          await supabase
            .from("profile_references")
            .delete()
            .eq("profile_id", user.id);
          const { error: refError } = await supabase
            .from("profile_references")
            .insert(
              validRefs.map((r) => ({
                profile_id: user.id,
                name: r.name.trim(),
                relationship: r.relationship.trim(),
                phone: normalizePhone(r.phone),
                email: r.email?.trim() || null,
              })),
            );
          if (refError) console.error("profile_creation.save_references_failed");
        }
      }

      // Save checkboxes to localStorage for persistence
      localStorage.setItem(`checkbox_agree_terms_${user.id}`, String(finalData.agree_terms || false));
      localStorage.setItem(`checkbox_agree_privacy_${user.id}`, String(finalData.agree_privacy || false));
      localStorage.setItem(`checkbox_understand_review_${user.id}`, String(finalData.understand_review || false));
      localStorage.setItem(`checkbox_affirm_truthful_${user.id}`, String(finalData.affirm_truthful || false));

      // Save dealbreaker questions (silent - main success toast will show)
      if (finalData.dealbreaker_questions !== undefined) {
        await saveQuestions({
          profileId: user.id,
          questions: finalData.dealbreaker_questions,
          silent: true,
        });
      }

      toast.success("Profile created successfully!");
      navigate("/dashboard");
    } catch (error: any) {
      console.error("profile_creation.create_failed");
      toast.error(error.message || "Failed to create profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const progressPercentage = ((currentStep + 1) / STEPS.length) * 100;

  // Custom sidebar component for profile editing with navigation interception
  const ProfileEditSidebar = () => {
    const { open } = useSidebar();

    const menuItems = [
      { title: "Dashboard", url: "/dashboard", icon: Home },
      { title: "My Profile", url: "/profile/create", icon: UserIcon },
      { title: "My Matches", url: "/dashboard/matches", icon: Heart },
      { title: "Settings", url: "/dashboard/settings", icon: Settings },
    ];

    return (
      <Sidebar className={open ? "w-60" : "w-14"} collapsible="icon">
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {menuItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavigationAttempt(item.url);
                        }}
                        className={`hover:bg-muted/50 ${
                          location.pathname === item.url ? 'bg-muted text-primary font-medium' : ''
                        }`}
                      >
                        <item.icon className="h-4 w-4" />
                        {open && <span>{item.title}</span>}
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    );
  };

  const renderContent = () => (
    <div className="container mx-auto max-w-3xl px-4 py-12">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-foreground">
            Complete Your Profile
          </h1>
          <p className="text-muted-foreground">
            Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
          </p>
          
          <div className="mt-4">
            <Progress value={progressPercentage} className="h-2" />
          </div>
        </div>

        {/* Quick Import - Only show on first step and when not editing existing */}
        {currentStep === 0 && !isEditingExisting && (
          <div className="mb-6">
            <QuickImport onExtract={handleQuickImport} />
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{STEPS[currentStep].title}</CardTitle>
            <CardDescription>{STEPS[currentStep].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {currentStep === 0 && (
              <Step1BasicInfo
                data={formData}
                onComplete={handleStepComplete}
                saving={saving}
              />
            )}
            
            {currentStep === 1 && (
              <Step2ReligiousProfile
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
              />
            )}
            
            {currentStep === 2 && (
              <Step3EducationCareer
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
              />
            )}
            
            {currentStep === 3 && (
              <Step4LifestylePersonality
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
              />
            )}
            
            {currentStep === 4 && (
              <Step5Preferences
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
              />
            )}
            
            {currentStep === 5 && (
              <Step6ContactReview
                data={formData}
                onComplete={handleFinalSubmit}
                onBack={handleBack}
                onEditStep={setCurrentStep}
                saving={saving}
                isEditingExisting={isEditingExisting}
              />
            )}
          </CardContent>
        </Card>
      </div>
  );

  if (isEditingExisting) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <ProfileEditSidebar />
          <div className="flex-1 flex flex-col">
            <DashboardHeader
              title="My Profile"
              profilePicture={formData.profile_picture_url}
              firstName={formData.first_name}
            />
            <main className="flex-1">
              {renderContent()}
            </main>
            <Footer />
          </div>
        </div>

        <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Would you like to save them before leaving?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleDiscardAndNavigate}>
                Discard Changes
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleSaveAndNavigate}>
                Save & Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SidebarProvider>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        {renderContent()}
        <Footer />
      </div>

      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save them before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardAndNavigate}>
              Discard Changes
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveAndNavigate}>
              Save & Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
